import { useEffect, useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { Transaction, formatCurrency, formatDate, CATEGORY_LABELS } from '@/lib/index';

const CATEGORY_ICONS: Record<string, string> = {
  salary: '💰',
  food: '🍔',
  transport: '🚗',
  entertainment: '🎬',
  shopping: '🛍️',
  utilities: '💡',
  health: '🏥',
  investment: '📈',
  transfer: '↔️',
  fee: '📋',
  other: '📦',
};

export default function DashboardTransactions() {
  const { account } = useAuth();
  const { t } = useI18n();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit' | 'transfer'>('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (!account) return;

    const fetchTransactions = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('transactions')
          .select('*')
          .eq('account_id', account.id)
          .order('created_at', { ascending: false })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

        if (typeFilter === 'credit') {
          query = query.in('type', ['credit', 'transfer_in', 'salary', 'deposit', 'interest', 'refund']);
        } else if (typeFilter === 'debit') {
          query = query.in('type', ['debit', 'payment', 'withdrawal', 'fee']);
        } else if (typeFilter === 'transfer') {
          query = query.in('type', ['transfer_in', 'transfer_out']);
        }

        if (search.trim()) {
          query = query.or(`description.ilike.%${search}%,reference.ilike.%${search}%`);
        }

        const { data, error } = await query;

        if (error) throw error;
        setTransactions((data ?? []) as Transaction[]);
      } catch (error) {
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [account, typeFilter, search, page]);

  const totalIncome = transactions
    .filter((tx) => ['credit', 'transfer_in', 'salary', 'deposit', 'interest', 'refund'].includes(tx.type))
    .reduce((sum, tx) => sum + tx.amount, 0);

  const totalExpenses = transactions
    .filter((tx) => ['debit', 'transfer_out', 'payment', 'withdrawal', 'fee'].includes(tx.type))
    .reduce((sum, tx) => sum + tx.amount, 0);

  const netBalance = totalIncome - totalExpenses;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleFilterChange = (filter: 'all' | 'credit' | 'debit' | 'transfer') => {
    setTypeFilter(filter);
    setPage(0);
  };

  const handleExportCSV = () => {
    const headers = ['Data', 'Opis', 'Kategoria', 'Kwota', 'Saldo po', 'Status'];
    const rows = transactions.map((tx) => {
      const isCredit = ['credit', 'transfer_in', 'salary', 'deposit', 'interest', 'refund'].includes(tx.type);
      return [
        formatDate(tx.created_at),
        tx.description,
        CATEGORY_LABELS[tx.category || 'other'] || CATEGORY_LABELS.other,
        `${isCredit ? '+' : '-'}${tx.amount} ${tx.currency}`,
        tx.balance_after ? formatCurrency(tx.balance_after, tx.currency) : '-',
        tx.status,
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transakcje_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <DashboardLayout title={t('tx_title')}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-foreground">{t('tx_title')}</h1>
          <Button variant="outline" className="gap-2" onClick={handleExportCSV}>
            <Download className="w-4 h-4" />
            Eksportuj CSV
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border/50 bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-success-light flex items-center justify-center">
                  <ArrowDownLeft className="w-6 h-6 text-success" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">{t('tx_income')}</p>
                  <p className="text-2xl font-bold text-success">{formatCurrency(totalIncome)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50 bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-danger-light flex items-center justify-center">
                  <ArrowUpRight className="w-6 h-6 text-danger" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">{t('tx_expenses')}</p>
                  <p className="text-2xl font-bold text-danger">{formatCurrency(totalExpenses)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50 bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-info-light flex items-center justify-center">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Saldo netto</p>
                  <p className={`text-2xl font-bold ${netBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(netBalance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border/50 bg-card">
          <CardContent className="p-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Szukaj transakcji..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 h-11 bg-background border-border"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                variant={typeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('all')}
                className="transition-all"
              >
                {t('tx_all')}
              </Button>
              <Button
                variant={typeFilter === 'credit' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('credit')}
                className="transition-all"
              >
                {t('tx_income')}
              </Button>
              <Button
                variant={typeFilter === 'debit' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('debit')}
                className="transition-all"
              >
                {t('tx_expense')}
              </Button>
              <Button
                variant={typeFilter === 'transfer' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('transfer')}
                className="transition-all"
              >
                Przelewy
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card">
          <CardHeader>
            <CardTitle className="text-xl">Historia transakcji</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
                    <div className="w-12 h-12 bg-muted rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-1/2" />
                      <div className="h-3 bg-muted rounded w-1/3" />
                    </div>
                    <div className="h-5 bg-muted rounded w-24" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-5xl mb-4">📭</p>
                <p className="text-lg font-semibold text-foreground mb-2">Brak transakcji</p>
                <p className="text-sm text-muted-foreground">
                  {search || typeFilter !== 'all' ? 'Nie znaleziono transakcji spełniających kryteria' : 'Nie masz jeszcze żadnych transakcji'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx, i) => {
                  const isCredit = ['credit', 'transfer_in', 'salary', 'deposit', 'interest', 'refund'].includes(tx.type);
                  const categoryLabel = CATEGORY_LABELS[tx.category || 'other'] || CATEGORY_LABELS.other;
                  const categoryIcon = CATEGORY_ICONS[tx.category || 'other'] || CATEGORY_ICONS.other;

                  return (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03, duration: 0.3 }}
                      className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/50 transition-all cursor-pointer group border border-transparent hover:border-border/50"
                    >
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${
                          isCredit ? 'bg-success-light' : 'bg-danger-light'
                        }`}
                      >
                        {isCredit ? (
                          <ArrowDownLeft className="w-6 h-6 text-success" />
                        ) : (
                          <ArrowUpRight className="w-6 h-6 text-danger" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate mb-1">
                          {tx.description}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <Badge variant="secondary" className="text-xs px-2 py-0">
                            {categoryIcon} {categoryLabel}
                          </Badge>
                          <span>{formatDate(tx.created_at)}</span>
                          {tx.reference && <span className="font-mono">Ref: {tx.reference}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0 space-y-1">
                        <p
                          className={`text-base font-bold ${
                            isCredit ? 'text-success' : 'text-danger'
                          }`}
                        >
                          {isCredit ? '+' : '-'}
                          {formatCurrency(Math.abs(tx.amount), tx.currency)}
                        </p>
                        {tx.balance_after !== null && tx.balance_after !== undefined && (
                          <p className="text-xs text-muted-foreground">
                            Saldo: {formatCurrency(tx.balance_after, tx.currency)}
                          </p>
                        )}
                        <Badge
                          variant={
                            tx.status === 'completed'
                              ? 'default'
                              : tx.status === 'pending'
                              ? 'secondary'
                              : 'destructive'
                          }
                          className="text-xs"
                        >
                          {tx.status === 'completed' ? 'Zrealizowana' : tx.status === 'pending' ? 'Oczekująca' : 'Odrzucona'}
                        </Badge>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {!loading && transactions.length > 0 && (
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Wstecz
                </Button>
                <span className="text-sm text-muted-foreground">
                  Strona {page + 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={transactions.length < PAGE_SIZE}
                  className="gap-2"
                >
                  Dalej
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}